import { UserName } from "@/components/UserName";
import { useBackState } from "@/hooks/useScrollMemory";
import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Loader2, Inbox, Package, Search, ClipboardCheck, Truck, ChevronRight, UserCheck, PlayCircle, CheckCircle2, XCircle, Send, MessageCircleQuestion } from "lucide-react";

const STATUS = { new: "bg-amber-500", in_progress: "bg-blue-600", resolved: "bg-emerald-600", cancelled: "bg-slate-400" };
const STATUS_LABEL = { new: "NEW", in_progress: "IN PROGRESS", resolved: "RESOLVED", cancelled: "CANCELLED" };
const URGENCY = { high: "bg-[#B01E28] text-white", normal: "bg-slate-100 text-slate-600", low: "bg-slate-100 text-slate-400" };
const OUTCOME_LABEL = { found_in_stock: "Found in stock", not_available: "Not available", sourced_externally: "Sourced externally", verified_correct: "Verified — correct", verified_discrepancy: "Verified — discrepancy", other: "Other", assessed: "Answered — it has been rated", dismissed: "Closed without a rating" };

const EMPTY = { type: "part", urgency: "normal", part_needed: "", category_id: "", oem_number: "", quantity: 1, vehicle_ref: "", what_to_check: "", notes: "", linked_part: "", linked_donor: "" };

export default function RequestsPage() {
  const { user, has } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState({ outcomes: [], can_manage: false });
  const canManage = user?.role === "Admin" || has("manage_requests");

  const [rows, setRows] = useState([]);
  const [more, setMore] = useState(null);   // showing X of Y
  const [loading, setLoading] = useState(true);
  const [f, setF] = useBackState("filters", { status: "all", type: "all", urgency: "all", mine: false });

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [sel, setSel] = useState(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resForm, setResForm] = useState({ outcome: "found_in_stock", note: "", resolved_part: "" });

  // SHOWING X OF Y — the list says how many there are and pages through all of them
  // (his instruction 2026-10-14). "Show more" adds to what is already on screen.
  const load = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const p = { paged: true, page, page_size: 50 };
      if (f.status !== "all") p.status = f.status;
      if (f.type !== "all") p.type = f.type;
      if (f.urgency !== "all") p.urgency = f.urgency;
      if (f.mine) p.mine = true;
      const { data } = await api.get("/requests", { params: p });
      setRows((prev) => (page > 1 ? [...prev, ...(data.items || [])] : (data.items || [])));
      setMore({ total: data.total || 0, shown: data.shown || 0, page: data.page || 1, has_more: !!data.has_more });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [f]);

  useEffect(() => { api.get("/requests/meta").then(({ data }) => setMeta(data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  // THE ANSWER GOES BACK TO WHOEVER ASKED — they are the one holding the customer. A line in their
  // own view, on top of the in-app notification the backend already sends.
  const [answered, setAnswered] = useState(0);
  useEffect(() => {
    api.get("/requests/open-count").then(({ data }) => setAnswered(data.answered_for_me || 0)).catch(() => {});
  }, []);
  const showAnswers = async () => {
    setF({ status: "all", type: "assess", urgency: "all", mine: true });
    try { await api.post("/requests/assess/seen"); } catch { /* the badge is not the record */ }
    setAnswered(0);
  };

  // open submit dialog pre-linked from a part/donor detail page
  useEffect(() => {
    if (params.get("new")) {
      const t = params.get("new") === "verify" ? "verify" : "part";
      setForm({ ...EMPTY, type: t, linked_part: params.get("part") || "", linked_donor: params.get("donor") || "" });
      setOpen(true);
      const sp = new URLSearchParams(params); sp.delete("new"); sp.delete("part"); sp.delete("donor"); setParams(sp, { replace: true });
    }
    // eslint-disable-next-line
  }, [params]);

  const submit = async () => {
    if (form.type === "part" && !form.part_needed.trim()) { toast.error("Describe the part needed."); return; }
    if (form.type === "verify" && !form.what_to_check.trim()) { toast.error("Describe what to check."); return; }
    setSaving(true);
    try {
      await api.post("/requests", { ...form, quantity: Number(form.quantity) || 1, category_id: form.category_id || null, linked_part: form.linked_part || null, linked_donor: form.linked_donor || null });
      toast.success("Request submitted");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const act = async (id, path, body, msg) => {
    try { const { data } = await api.post(`/requests/${id}/${path}`, body || {}); toast.success(msg); setSel(data); load(); return data; }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const doResolve = async () => {
    setSaving(true);
    const r = await act(sel.id, "resolve", { outcome: resForm.outcome, note: resForm.note, resolved_part: resForm.resolved_part || null }, "Request resolved");
    setSaving(false);
    if (r) { setResolveOpen(false); setResForm({ outcome: "found_in_stock", note: "", resolved_part: "" }); }
  };

  const TypeIcon = ({ t }) => t === "verify" ? <ClipboardCheck className="w-3.5 h-3.5" />
    : t === "assess" ? <MessageCircleQuestion className="w-3.5 h-3.5" /> : <Package className="w-3.5 h-3.5" />;
  const TYPE_WORD = { verify: "Verify", assess: "Asking", part: "Part" };
  const TYPE_TONE = { verify: "bg-teal-600", assess: "bg-[#C76714]", part: "bg-indigo-600" };
  const title = (r) => (r.type === "part" ? r.part_needed : r.what_to_check);

  return (
    <div data-testid="requests-page">
      <div className="flex items-center justify-between mb-5 gap-2 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight flex items-center gap-2"><Inbox className="w-5 h-5 text-[#B01E28]" />Requests</h1>
          <p className="text-sm text-slate-500">{canManage ? "Work the queue — part requests and verify/check tasks from the team." : "Ask the parts team for a part or to check something. Track your requests here."}</p>
        </div>
        <Button data-testid="submit-request-btn" onClick={() => { setForm(EMPTY); setOpen(true); }} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white"><Plus className="w-4 h-4 mr-1.5" />Submit a request</Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {answered > 0 && (
          <button type="button" data-testid="requests-answered-banner" onClick={showAnswers}
            className="w-full rounded-lg border-2 border-emerald-300 bg-emerald-50 px-3 py-2 text-left text-sm font-semibold text-emerald-800 hover:bg-emerald-100 transition-colors">
            <CheckCircle2 className="inline w-4 h-4 mr-1.5 -mt-0.5" />
            {answered} thing{answered === 1 ? "" : "s"} you asked about {answered === 1 ? "has" : "have"} been answered — show {answered === 1 ? "it" : "them"}
          </button>
        )}
        <FilterSelect testid="filter-status" value={f.status} onChange={(v) => setF({ ...f, status: v })} label="Status" opts={[["all", "All statuses"], ["new", "New"], ["in_progress", "In progress"], ["resolved", "Resolved"], ["cancelled", "Cancelled"]]} />
        <FilterSelect testid="filter-type" value={f.type} onChange={(v) => setF({ ...f, type: v })} label="Type" opts={[["all", "All types"], ["part", "Part request"], ["verify", "Verify / check"], ["assess", "Somebody's asking"]]} />
        <FilterSelect testid="filter-urgency" value={f.urgency} onChange={(v) => setF({ ...f, urgency: v })} label="Urgency" opts={[["all", "All urgency"], ["high", "High"], ["normal", "Normal"], ["low", "Low"]]} />
        {more && (
          <span data-testid="requests-showing" className="self-center font-mono text-[11px] text-slate-500">
            {more.total === 0 ? "no requests"
              : more.shown >= more.total ? `showing all ${more.total}`
                : `showing ${more.shown} of ${more.total}`}
          </span>
        )}
        {canManage && (
          <button data-testid="filter-mine" onClick={() => setF({ ...f, mine: !f.mine })} className={`text-xs font-mono px-3 py-2 rounded-lg border ${f.mine ? "bg-[#1F1F1F] text-white border-[#1F1F1F]" : "bg-white border-slate-200 text-slate-600"}`}>{f.mine ? "Mine only" : "All requests"}</button>
        )}
      </div>

      <div className="border border-slate-200 bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? <div className="p-8 text-center font-mono text-xs text-slate-400">LOADING…</div>
          : rows.length === 0 ? <div className="p-10 text-center font-mono text-xs text-slate-400">NO REQUESTS</div>
          : rows.map((r) => (
            <div key={r.id} data-testid={`request-row-${r.request_number}`} onClick={() => setSel(r)} className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3.5 border-t border-slate-100 hover:bg-slate-50/70 cursor-pointer transition-colors">
              <span className="font-mono text-xs font-bold text-slate-500 w-24">{r.request_number}</span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={`rounded-md text-[9px] uppercase font-mono ${TYPE_TONE[r.type] || "bg-indigo-600"} text-white`}><TypeIcon t={r.type} /><span className="ml-1">{TYPE_WORD[r.type] || r.type}</span></Badge>
                  <span className="text-sm font-medium truncate">{title(r)}</span>
                  {/* THE DEMAND SIGNAL. Three people waiting on the same engine is the one to walk
                      out to first, which is why this queue sorts most-asked first. */}
                  {r.type === "assess" && r.ask_count > 1 && (
                    <span data-testid={`request-asks-${r.request_number}`}
                      className="rounded-md bg-[#B01E28] px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-white">
                      asked {r.ask_count} times
                    </span>
                  )}
                  {/* THE ANSWER ON THE ROW, not only inside the dialog. The person who asked is
                      holding a customer — making them open a record to find out is a second
                      errand at the worst possible moment. */}
                  {r.type === "assess" && r.resolution && (
                    <span data-testid={`request-answer-${r.request_number}`}
                      className={`rounded-md px-1.5 py-0.5 font-mono text-[9px] font-bold uppercase text-white ${r.resolution.outcome === "dismissed" ? "bg-slate-500" : "bg-emerald-600"}`}>
                      {r.resolution.outcome === "dismissed"
                        ? `closed — ${r.resolution.reason_label}`
                        : `answered — ${r.resolution.state_label}`}
                    </span>
                  )}
                  {r.type === "assess" && r.needs_more_than_a_look && (
                    <span data-testid={`request-still-cant-tell-${r.request_number}`}
                      className="rounded-md border border-slate-300 px-1.5 py-0.5 font-mono text-[9px] uppercase text-slate-500">
                      somebody went · still can’t tell
                    </span>
                  )}
                </div>
                <div className="font-mono text-[10px] text-slate-400 mt-0.5">
                  {r.type === "assess"
                    ? `asked by ${(r.askers || []).map((a) => a.name).filter(Boolean).join(", ") || r.requested_by_name}`
                    : `by ${r.requested_by_name || r.requested_by}`}
                  {r.assigned_to ? ` · @${r.assigned_to}` : ""}{r.linked_part ? ` · 🔗 ${r.linked_part.tag_code || r.linked_part.short_code}` : ""}{r.linked_donor ? ` · 🔗 ${r.linked_donor.stock_number}` : ""}
                </div>
              </div>
              <Badge className={`rounded-md text-[9px] uppercase font-mono ${URGENCY[r.urgency]}`}>{r.urgency}</Badge>
              <Badge className={`rounded-md text-[9px] uppercase font-mono text-white ${STATUS[r.status]}`}>{STATUS_LABEL[r.status]}</Badge>
              <ChevronRight className="w-4 h-4 text-slate-300 hidden sm:block" />
            </div>
          ))}
        {more?.has_more && (
          <button type="button" data-testid="requests-load-more" onClick={() => load((more.page || 1) + 1)}
            className="w-full border-t border-slate-100 py-3 font-mono text-xs text-slate-600 hover:text-[#B01E28]">
            Show more — {more.total - more.shown} still to come
          </button>
        )}
      </div>

      {/* Submit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-lg" data-testid="request-dialog">
          <DialogHeader><DialogTitle className="font-display">Submit a request</DialogTitle><DialogDescription>Ask the parts team for a part or to verify/check something. Optionally link a part or donor for context.</DialogDescription></DialogHeader>
          <div className="space-y-3 max-h-[65dvh] overflow-y-auto pr-1">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="request-type" className="rounded-lg border-slate-200 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-lg"><SelectItem value="part">Part request</SelectItem><SelectItem value="verify">Verify / check</SelectItem></SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Urgency</Label>
                <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                  <SelectTrigger data-testid="request-urgency" className="rounded-lg border-slate-200 mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-lg"><SelectItem value="low">Low</SelectItem><SelectItem value="normal">Normal</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            {form.type === "part" ? (
              <>
                <Field label="Part needed *"><Input data-testid="request-part-needed" value={form.part_needed} onChange={(e) => setForm({ ...form, part_needed: e.target.value })} placeholder="e.g. Alternator for Cascadia DD15" /></Field>
                <div className="grid grid-cols-2 gap-2">
                  <Field label="OEM / part number"><Input data-testid="request-oem" value={form.oem_number} onChange={(e) => setForm({ ...form, oem_number: e.target.value })} /></Field>
                  <Field label="Quantity"><Input data-testid="request-qty" type="number" min="1" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></Field>
                </div>
                <Field label="Vehicle / VIN / Build / PO (optional)"><Input data-testid="request-vehicle" value={form.vehicle_ref} onChange={(e) => setForm({ ...form, vehicle_ref: e.target.value })} /></Field>
              </>
            ) : (
              <Field label="What to check / verify *"><Textarea data-testid="request-what-to-check" value={form.what_to_check} onChange={(e) => setForm({ ...form, what_to_check: e.target.value })} placeholder="e.g. Confirm FAS-004217 is actually in Bin 12" /></Field>
            )}
            <div className="grid grid-cols-2 gap-2">
              <Field label="Link a part (FAS tag/code)"><Input data-testid="request-link-part" value={form.linked_part} onChange={(e) => setForm({ ...form, linked_part: e.target.value })} /></Field>
              <Field label="Link a truck (stock #)"><Input data-testid="request-link-donor" value={form.linked_donor} onChange={(e) => setForm({ ...form, linked_donor: e.target.value })} /></Field>
            </div>
            <Field label="Notes"><Textarea data-testid="request-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="request-submit-confirm" disabled={saving} onClick={submit} className="rounded-lg bg-[#E0781A] hover:bg-[#C76714] text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1.5" />Submit</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail / work drawer */}
      <Dialog open={!!sel} onOpenChange={(v) => !v && setSel(null)}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-lg" data-testid="request-detail-dialog">
          {sel && (<>
            <DialogHeader><DialogTitle className="font-display flex items-center gap-2 flex-wrap">{sel.request_number}
              <Badge className={`rounded-md text-[9px] uppercase font-mono text-white ${STATUS[sel.status]}`}>{STATUS_LABEL[sel.status]}</Badge>
              <Badge className={`rounded-md text-[9px] uppercase font-mono ${URGENCY[sel.urgency]}`}>{sel.urgency}</Badge>
            </DialogTitle><DialogDescription className="sr-only">Request detail, activity trail and work actions.</DialogDescription></DialogHeader>
            <div className="space-y-3 max-h-[60dvh] overflow-y-auto pr-1 text-sm">
              <p className="font-medium">{title(sel)}</p>
              {/* WHO IS WAITING, AND FOR WHAT. Each asker keeps their own note: "needs it by
                  Friday" and "just checking" are different urgencies. */}
              {sel.type === "assess" && (
                <div data-testid="request-askers" className="rounded-lg border border-[#E0781A]/50 bg-[#FFF7ED] p-2.5 space-y-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#8a4b0b]">
                    {sel.ask_count} ask{sel.ask_count === 1 ? "" : "s"} · {sel.component_label} on {sel.stock_number}
                  </div>
                  {(sel.askers || []).map((a, i) => (
                    <div key={i} data-testid={`request-asker-${i}`} className="text-[12px] text-[#8a5a12]">
                      <b>{a.name}</b>{a.role ? ` (${a.role})` : ""} · {(a.at || "").slice(0, 10)}
                      {a.note ? <> — “{a.note}”</> : null}
                      {a.from_query ? <span className="font-mono text-[10px] text-slate-400"> · searched “{a.from_query}”</span> : null}
                    </div>
                  ))}
                  {(sel.looked_cant_tell || []).map((l, i) => (
                    <div key={`c${i}`} data-testid={`request-cant-tell-${i}`} className="text-[12px] text-slate-600">
                      <b>{l.name}</b> went out and still couldn’t tell · {(l.at || "").slice(0, 10)} — needs more than a look
                    </div>
                  ))}
                  {sel.linked_donor && (
                    <button data-testid="request-ask-open-truck" onClick={() => navigate(`/donors/${sel.linked_donor.id}`)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-[#C76714] hover:underline">
                      <Truck className="w-3.5 h-3.5" />rate it on {sel.stock_number}<ChevronRight className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )}
              <div className="font-mono text-[11px] text-slate-500 space-y-0.5">
                {sel.type === "part" && (sel.oem_number || sel.quantity) && <div>OEM {sel.oem_number || "—"} · qty {sel.quantity}</div>}
                {sel.vehicle_ref && <div>Vehicle/PO: {sel.vehicle_ref}</div>}
                <div>Requested by {sel.requested_by_name || sel.requested_by}{sel.assigned_to ? ` · assigned to ${sel.assigned_to}` : ""}</div>
              </div>
              {(sel.linked_part || sel.linked_donor) && (
                <div className="flex flex-wrap gap-2">
                  {sel.linked_part && <button data-testid="request-open-part" onClick={() => navigate(`/parts/${sel.linked_part.id}`)} className="inline-flex items-center gap-1 text-xs text-[#E0781A] hover:underline"><Package className="w-3.5 h-3.5" />{sel.linked_part.tag_code || sel.linked_part.short_code} · {sel.linked_part.name}<ChevronRight className="w-3 h-3" /></button>}
                  {sel.linked_donor && <button data-testid="request-open-donor" onClick={() => navigate(`/donors/${sel.linked_donor.id}`)} className="inline-flex items-center gap-1 text-xs text-[#E0781A] hover:underline"><Truck className="w-3.5 h-3.5" />{sel.linked_donor.stock_number}<ChevronRight className="w-3 h-3" /></button>}
                </div>
              )}
              {sel.notes && <p className="text-slate-600 border-l-2 border-slate-200 pl-2">{sel.notes}</p>}
              {sel.resolution && (
                <div className={`border rounded-lg p-3 ${sel.resolution.outcome === "dismissed" ? "border-slate-300 bg-slate-50" : "border-emerald-200 bg-emerald-50"}`} data-testid="request-resolution">
                  <div className={`font-bold text-xs uppercase ${sel.resolution.outcome === "dismissed" ? "text-slate-600" : "text-emerald-800"}`}>
                    {OUTCOME_LABEL[sel.resolution.outcome] || sel.resolution.outcome}
                    {sel.resolution.state_label ? ` — ${sel.resolution.state_label}` : ""}
                    {sel.resolution.reason_label ? ` — ${sel.resolution.reason_label}` : ""}
                  </div>
                  {sel.resolution.note && <div className="text-slate-700 text-sm mt-0.5">{sel.resolution.note}</div>}
                  <div className="font-mono text-[10px] text-slate-400 mt-1">by <UserName email={sel.resolution.by} /> · {(sel.resolution.at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
              )}
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Activity</div>
                <ol className="relative border-l border-slate-200 ml-1.5">
                  {(sel.activity || []).map((a, i) => (
                    <li key={i} className="ml-3 pb-2 last:pb-0">
                      <span className="absolute -left-[5px] mt-1 w-2 h-2 rounded-full bg-[#E0781A] border border-white" />
                      <div className="text-xs"><span className="font-medium capitalize">{a.action.replace("_", " ")}</span> {a.detail && <span className="text-slate-500">— {a.detail}</span>}</div>
                      <div className="font-mono text-[10px] text-slate-400"><UserName email={a.by} /> · {(a.at || "").slice(0, 16).replace("T", " ")}</div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
            <DialogFooter className="flex-wrap gap-2">
              {canManage && !["resolved", "cancelled"].includes(sel.status) && (<>
                <Button data-testid="request-assign-me" size="sm" variant="outline" className="rounded-lg border-slate-300" onClick={() => act(sel.id, "assign", {}, "Assigned to you")}><UserCheck className="w-3.5 h-3.5 mr-1" />Assign to me</Button>
                {sel.status === "new" && <Button data-testid="request-start" size="sm" variant="outline" className="rounded-lg border-blue-300 text-blue-700" onClick={() => act(sel.id, "status", { status: "in_progress" }, "Marked in progress")}><PlayCircle className="w-3.5 h-3.5 mr-1" />Start</Button>}
                <Button data-testid="request-resolve-open" size="sm" className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setResolveOpen(true)}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Resolve</Button>
              </>)}
              {!["resolved", "cancelled"].includes(sel.status) && (canManage || sel.requested_by === user?.email) && (
                <Button data-testid="request-cancel" size="sm" variant="outline" className="rounded-lg border-[#B01E28] text-[#B01E28] hover:bg-red-50" onClick={() => act(sel.id, "cancel", {}, "Request cancelled")}><XCircle className="w-3.5 h-3.5 mr-1" />Cancel</Button>
              )}
            </DialogFooter>
          </>)}
        </DialogContent>
      </Dialog>

      {/* Resolve dialog */}
      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent className="rounded-xl border-slate-200 sm:max-w-md" data-testid="resolve-dialog">
          <DialogHeader><DialogTitle className="font-display">Resolve request</DialogTitle><DialogDescription>Pick an outcome and write a note back to the requester. This is informational — it does not move inventory.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Field label="Outcome">
              <Select value={resForm.outcome} onValueChange={(v) => setResForm({ ...resForm, outcome: v })}>
                <SelectTrigger data-testid="resolve-outcome" className="rounded-lg border-slate-200"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-lg">{(meta.outcomes || []).map((o) => <SelectItem key={o} value={o}>{OUTCOME_LABEL[o] || o}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Note back to the requester"><Textarea data-testid="resolve-note" value={resForm.note} onChange={(e) => setResForm({ ...resForm, note: e.target.value })} /></Field>
            <Field label="Found part — link by FAS tag/code (optional)"><Input data-testid="resolve-part" value={resForm.resolved_part} onChange={(e) => setResForm({ ...resForm, resolved_part: e.target.value })} /></Field>
            <p className="text-[10px] text-slate-400">Resolving is informational — it does not reserve or move inventory. Use Carts & Orders to actually pull a part.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg border-slate-200" onClick={() => setResolveOpen(false)}>Cancel</Button>
            <Button data-testid="resolve-confirm" disabled={saving} onClick={doResolve} className="rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resolve"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children }) {
  return <div><Label className="text-xs font-bold uppercase tracking-wider text-slate-500">{label}</Label><div className="mt-1">{children}</div></div>;
}
function FilterSelect({ testid, value, onChange, label, opts }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="rounded-lg border-slate-200 w-40 h-9 text-sm"><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent className="rounded-lg">{opts.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
}
